/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

export function JournalVoucher7Info({ formData }) {
  const { values, setFieldValue, containView } = formData;
  const [tabIndex, setTabIndex] = useState(0);

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");
  const num = (v) => Number(v) || 0;

  // --- LOGIC CALCULATIONS ---
  const [totalsA, setTotalsA] = useState({ creditValues: Array(17).fill(0), subTotalCredit: 0, otherVoucherValues: Array(4).fill(0), totalCost: 0 });
  const [totalsB, setTotalsB] = useState({ creditValues: Array(17).fill(0), subTotalCredit: 0, otherVoucherValues: Array(4).fill(0), totalCost: 0 });

  useEffect(() => {
    // 1. Part I Calculations
    const calcRows = (rows) => (rows || []).map(r => {
      const sub = (r.creditValues || []).reduce((a, b) => a + num(b), 0);
      const otherTotal = (r.otherVoucherValues || []).reduce((a, b) => a + num(b), 0);
      return { ...r, subTotalCredit: sub, totalCost: sub + otherTotal };
    });

    const newRowsA = calcRows(values.part1.rowsA);
    const newRowsB = calcRows(values.part1.rowsB);
    
    const getTotals = (rows) => {
      const t = { creditValues: Array(17).fill(0), subTotalCredit: 0, otherVoucherValues: Array(4).fill(0), totalCost: 0 };
      rows.forEach(r => {
        (r.creditValues || []).forEach((v, i) => t.creditValues[i] += num(v));
        (r.otherVoucherValues || []).forEach((v, i) => t.otherVoucherValues[i] += num(v));
        t.subTotalCredit += r.subTotalCredit || 0;
        t.totalCost += r.totalCost || 0;
      });
      return t;
    };

    const tA = getTotals(newRowsA);
    const tB = getTotals(newRowsB);
    setTotalsA(tA);
    setTotalsB(tB);

    const grandTotals = {
      creditValues: tA.creditValues.map((v, i) => v + tB.creditValues[i]),
      subTotalCredit: tA.subTotalCredit + tB.subTotalCredit,
      otherVoucherValues: tA.otherVoucherValues.map((v, i) => v + tB.otherVoucherValues[i]),
      totalCost: tA.totalCost + tB.totalCost
    };

    // 2. Part II Calculations
    const newRows2 = (values.part2.rows || []).map(r => {
      const sub = (r.elementValues || []).reduce((a, b) => a + num(b), 0);
      return { ...r, subTotal: sub, totalCost: sub - num(r.internalTurnover) };
    });

    const totals2 = {
      elementValues: Array(5).fill(0),
      subTotal: 0, internalTurnover: 0, totalCost: 0,
      accumulatedValues: values.part2.totals?.accumulatedValues || Array(5).fill(0),
      accumulatedSubTotal: values.part2.totals?.accumulatedSubTotal || 0,
      accumulatedInternalTurnover: values.part2.totals?.accumulatedInternalTurnover || 0,
      accumulatedTotalCost: values.part2.totals?.accumulatedTotalCost || 0
    };

    newRows2.forEach(r => {
      (r.elementValues || []).forEach((v, i) => totals2.elementValues[i] += num(v));
      totals2.subTotal += r.subTotal;
      totals2.internalTurnover += num(r.internalTurnover);
      totals2.totalCost += r.totalCost;
    });

    // 3. Part III Calculations
    const newRows3 = (values.part3.rows || []).map(r => {
      const sub = (r.detailValues || []).reduce((a, b) => a + num(b), 0);
      return { ...r, total: sub };
    });

    const totals3 = { columnTotals: Array(9).fill(0), grandTotal: 0 };
    newRows3.forEach(r => {
      (r.detailValues || []).forEach((v, i) => totals3.columnTotals[i] += num(v));
      totals3.grandTotal += r.total;
    });

    if (JSON.stringify(newRowsA) !== JSON.stringify(values.part1.rowsA)) setFieldValue("part1.rowsA", newRowsA);
    if (JSON.stringify(newRowsB) !== JSON.stringify(values.part1.rowsB)) setFieldValue("part1.rowsB", newRowsB);
    if (JSON.stringify(grandTotals) !== JSON.stringify(values.part1.totals)) setFieldValue("part1.totals", grandTotals);
    
    if (JSON.stringify(newRows2) !== JSON.stringify(values.part2.rows)) setFieldValue("part2.rows", newRows2);
    if (JSON.stringify(totals2) !== JSON.stringify(values.part2.totals)) setFieldValue("part2.totals", totals2);

    if (JSON.stringify(newRows3) !== JSON.stringify(values.part3.rows)) setFieldValue("part3.rows", newRows3);
    if (JSON.stringify(totals3.columnTotals) !== JSON.stringify(values.part3.columnTotals)) setFieldValue("part3.columnTotals", totals3.columnTotals);
    if (JSON.stringify(totals3.grandTotal) !== JSON.stringify(values.part3.grandTotal)) setFieldValue("part3.grandTotal", totals3.grandTotal);

  }, [
    JSON.stringify(values.part1.rowsA.map(r => [...r.creditValues, ...r.otherVoucherValues])),
    JSON.stringify(values.part1.rowsB.map(r => [...r.creditValues, ...r.otherVoucherValues])),
    JSON.stringify(values.part2.rows.map(r => [...r.elementValues, r.internalTurnover])),
    JSON.stringify(values.part3.rows.map(r => r.detailValues))
  ]);

  // --- ADD / REMOVE ROW HANDLERS ---
  const addRowA = () => {
      const newIdx = values.part1.rowsA.length + 1;
      setFieldValue("part1.rowsA", [...values.part1.rowsA, { index: newIdx, debitAccountCode: "", creditValues: Array(17).fill(0), subTotalCredit: 0, otherVoucherValues: Array(4).fill(0), totalCost: 0 }]);
  };
  const removeRowA = (idx) => {
      const next = values.part1.rowsA.filter((_, i) => i !== idx);
      setFieldValue("part1.rowsA", next.map((r, i) => ({ ...r, index: i + 1 })));
  };
  const addRowB = () => {
      const newIdx = values.part1.rowsB.length + 14; // B starts at 14 according to sample
      setFieldValue("part1.rowsB", [...values.part1.rowsB, { index: newIdx, debitAccountCode: "", creditValues: Array(17).fill(0), subTotalCredit: 0, otherVoucherValues: Array(4).fill(0), totalCost: 0 }]);
  };
  const removeRowB = (idx) => {
      const next = values.part1.rowsB.filter((_, i) => i !== idx);
      setFieldValue("part1.rowsB", next.map((r, i) => ({ ...r, index: i + 14 })));
  };
  const addRow2 = () => {
      setFieldValue("part2.rows", [...values.part2.rows, { index: values.part2.rows.length + 1, accountName: "", elementValues: Array(5).fill(0), subTotal: 0, internalTurnover: 0, totalCost: 0 }]);
  };
  const removeRow2 = (idx) => {
      const next = values.part2.rows.filter((_, i) => i !== idx);
      setFieldValue("part2.rows", next.map((r, i) => ({ ...r, index: i + 1 })));
  };
  const addRow3 = () => {
      setFieldValue("part3.rows", [...values.part3.rows, { index: values.part3.rows.length + 1, accountName: "", detailValues: Array(9).fill(0), total: 0 }]);
  };
  const removeRow3 = (idx) => {
      const next = values.part3.rows.filter((_, i) => i !== idx);
      setFieldValue("part3.rows", next.map((r, i) => ({ ...r, index: i + 1 })));
  };

  const s = {
    container: { fontFamily: '"Times New Roman", serif', fontSize: "11px", color: "#000", backgroundColor: "#fff" },
    th: { border: "1px solid #000", padding: "2px", backgroundColor: "#f8f9fa", fontWeight: "bold", textAlign: "center", textTransform: "uppercase" },
    td: { border: "1px solid #000", height: "24px" },
    input: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "right", padding: "0 2px", background: "transparent", fontSize: "11px" },
    inputCenter: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "center", background: "transparent", fontSize: "11px" },
    dotted: { border: "none", borderBottom: "1px dotted #000", outline: "none", padding: "0 5px", width: "120px", fontWeight: "bold", textAlign: "center" },
    title: { fontSize: "16px", fontWeight: "bold", textAlign: "center", color: "#1A2035", textTransform: "uppercase" },
    diagonal: {
        background: "linear-gradient(to top right, transparent 49.5%, #000 49.5%, #000 50.5%, transparent 50.5%)",
        position: "relative"
    },
    diagonalTop: { position: "absolute", top: "2px", right: "5px", fontSize: "9px" },
    diagonalBottom: { position: "absolute", bottom: "2px", left: "5px", fontSize: "9px", textAlign: "left" },
    removeBtn: { border: "none", background: "transparent", color: "red", cursor: "pointer", fontWeight: "bold", fontSize: "14px", padding: 0 }
  };

  return (
    <SoftBox style={s.container}>
      <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)} centered sx={{ mb: 2, background: "#f0f2f5" }}>
        <Tab label="Phần I: Tập hợp chi phí" />
        <Tab label="Phần II: Theo yếu tố" />
        <Tab label="Phần III: Chi tiết luân chuyển" />
      </Tabs>

      <Box p={2}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ fontWeight: "bold" }}>
                <div>Đơn vị: <input disabled={containView} style={s.dotted} value={values.unit} onChange={e => setFieldValue("unit", e.target.value)} /></div>
                <div>Địa chỉ: <input disabled={containView} style={{ ...s.dotted, width: "180px" }} value={values.address} onChange={e => setFieldValue("address", e.target.value)} /></div>
            </div>
            <div style={{ textAlign: "right", fontWeight: "bold" }}>
                <div>Mẫu số S04a7-DN</div>
                <div style={{ fontSize: "9px", fontStyle: "italic", fontWeight: "normal" }}>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 <br/> của Bộ trưởng Bộ Tài chính)</div>
            </div>
        </div>
      </Box>

      {/* PART I */}
      <TabPanel value={tabIndex} index={0}>
        <div style={s.title}>NHẬT KÝ CHỨNG TỪ SỐ 7</div>
        <div style={{ ...s.title, fontSize: "13px", marginTop: "5px" }}>Phần I: Tập hợp chi phí sản xuất, kinh doanh toàn doanh nghiệp</div>
        <div style={{ textAlign: "center", fontStyle: "italic", marginBottom: "10px" }}>Ghi Có các TK: 152, 153, 154, 214, 241, 242, 334, 335, 338, 352, 356, 621, 622, 623, 627</div>
        <div style={{ textAlign: "center", marginBottom: "10px" }}>Tháng <input disabled={containView} style={s.dotted} value={values.month} onChange={e => setFieldValue("month", e.target.value)} /> năm <input disabled={containView} style={s.dotted} value={values.year} onChange={e => setFieldValue("year", e.target.value)} /></div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "1850px" }}>
            <thead>
              <tr>
                <th style={{ ...s.th, ...s.diagonal, width: "130px", height: "80px" }} colSpan={2}>
                    <div style={s.diagonalTop}>Các TK Ghi có</div>
                    <div style={s.diagonalBottom}>Số TT / <br/> Các TK ghi Nợ</div>
                </th>
                <th style={s.th} colSpan={18}>Các TK Ghi có</th>
                <th style={s.th} colSpan={4}>Các TK phản ánh ở các NKCT khác</th>
                <th style={{ ...s.th, width: "100px" }} rowSpan={3}>Tổng cộng chi phí</th>
                {!containView && <th style={{ ...s.th, width: "40px" }} rowSpan={4}>#</th>}
              </tr>
              <tr>
                <th style={{ ...s.th, width: "40px" }} rowSpan={2}>Số TT</th>
                <th style={{ ...s.th, width: "100px" }} rowSpan={2}>Các TK ghi Nợ</th>
                {values.part1.creditAccountLabels.map((l, i) => (
                    <th key={i} style={{ ...s.th, width: "65px" }}><input disabled={containView} style={s.inputCenter} value={l} onChange={e => {
                        const next = [...values.part1.creditAccountLabels];
                        next[i] = e.target.value;
                        setFieldValue("part1.creditAccountLabels", next);
                    }}/></th>
                ))}
                <th style={{ ...s.th, width: "100px" }} rowSpan={2}>Cộng</th>
                <th style={s.th} colSpan={4}>NKCT số</th>
              </tr>
              <tr>
                {values.part1.creditAccountLabels.map((_, i) => <th key={i} style={s.th}>{i + 1}</th>)}
                {values.part1.otherVoucherLabels.map((l, i) => (
                    <th key={i} style={{ ...s.th, width: "80px" }}><input disabled={containView} style={s.inputCenter} value={l} onChange={e => {
                        const next = [...values.part1.otherVoucherLabels];
                        next[i] = e.target.value;
                        setFieldValue("part1.otherVoucherLabels", next);
                    }}/></th>
                ))}
              </tr>
              <tr style={{ background: "#eee", fontWeight: "bold" }}>
                  <th style={s.th}>A</th>
                  <th style={s.th}>B</th>
                  {Array.from({ length: 17 }).map((_, i) => <th key={i} style={s.th}>{i + 1}</th>)}
                  <th style={s.th}>18</th>
                  <th style={s.th}>19</th>
                  <th style={s.th}>20</th>
                  <th style={s.th}>21</th>
                  <th style={s.th}>22</th>
                  <th style={s.th}>23</th>
              </tr>
            </thead>
            <tbody>
              {values.part1.rowsA.map((r, i) => (
                  <tr key={i}>
                    <td style={s.td} align="center">{r.index}</td>
                    <td style={s.td}><input disabled={containView} style={{ ...s.input, textAlign: "left" }} value={r.debitAccountCode} onChange={e => setFieldValue(`part1.rowsA.${i}.debitAccountCode`, e.target.value)} /></td>
                    {r.creditValues.map((v, ci) => <td key={ci} style={s.td}><input disabled={containView} type="number" style={s.input} value={v} onChange={e => setFieldValue(`part1.rowsA.${i}.creditValues.${ci}`, e.target.value)} /></td>)}
                    <td style={{ ...s.td, textAlign: "right", fontWeight: "bold", background: "#f8f9fa" }}>{fmt(r.subTotalCredit)}</td>
                    {r.otherVoucherValues.map((v, ci) => <td key={ci} style={s.td}><input disabled={containView} type="number" style={s.input} value={v} onChange={e => setFieldValue(`part1.rowsA.${i}.otherVoucherValues.${ci}`, e.target.value)} /></td>)}
                    <td style={{ ...s.td, textAlign: "right", fontWeight: "bold", background: "#f0f2f5" }}>{fmt(r.totalCost)}</td>
                    {!containView && <td style={s.td} align="center"><button type="button" style={s.removeBtn} onClick={() => removeRowA(i)}>-</button></td>}
                  </tr>
              ))}
              <tr style={{ backgroundColor: "#e2e8f0", fontWeight: "bold" }}>
                  <td style={s.td} align="center">13</td>
                  <td style={s.td}>&nbsp; Cộng A</td>
                  {totalsA.creditValues.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                  <td style={{ ...s.td, textAlign: "right" }}>{fmt(totalsA.subTotalCredit)}</td>
                  {totalsA.otherVoucherValues.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                  <td style={{ ...s.td, textAlign: "right" }}>{fmt(totalsA.totalCost)}</td>
                  {!containView && <td style={s.td}></td>}
              </tr>
              {values.part1.rowsB.map((r, i) => (
                  <tr key={i}>
                    <td style={s.td} align="center">{r.index}</td>
                    <td style={s.td}><input disabled={containView} style={{ ...s.input, textAlign: "left" }} value={r.debitAccountCode} onChange={e => setFieldValue(`part1.rowsB.${i}.debitAccountCode`, e.target.value)} /></td>
                    {r.creditValues.map((v, ci) => <td key={ci} style={s.td}><input disabled={containView} type="number" style={s.input} value={v} onChange={e => setFieldValue(`part1.rowsB.${i}.creditValues.${ci}`, e.target.value)} /></td>)}
                    <td style={{ ...s.td, textAlign: "right", fontWeight: "bold", background: "#f8f9fa" }}>{fmt(r.subTotalCredit)}</td>
                    {r.otherVoucherValues.map((v, ci) => <td key={ci} style={s.td}><input disabled={containView} type="number" style={s.input} value={v} onChange={e => setFieldValue(`part1.rowsB.${i}.otherVoucherValues.${ci}`, e.target.value)} /></td>)}
                    <td style={{ ...s.td, textAlign: "right", fontWeight: "bold", background: "#f0f2f5" }}>{fmt(r.totalCost)}</td>
                    {!containView && <td style={s.td} align="center"><button type="button" style={s.removeBtn} onClick={() => removeRowB(i)}>-</button></td>}
                  </tr>
              ))}
              <tr style={{ backgroundColor: "#e2e8f0", fontWeight: "bold" }}>
                  <td style={s.td} align="center">24</td>
                  <td style={s.td}>&nbsp; Cộng B</td>
                  {totalsB.creditValues.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                  <td style={{ ...s.td, textAlign: "right" }}>{fmt(totalsB.subTotalCredit)}</td>
                  {totalsB.otherVoucherValues.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                  <td style={{ ...s.td, textAlign: "right" }}>{fmt(totalsB.totalCost)}</td>
                  {!containView && <td style={s.td}></td>}
              </tr>
              <tr style={{ backgroundColor: "#cbd5e1", fontWeight: "bold", fontSize: "12px" }}>
                  <td style={s.td} align="center">25</td>
                  <td style={s.td}>&nbsp; Tổng cộng (A+B)</td>
                  {values.part1.totals.creditValues.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                  <td style={{ ...s.td, textAlign: "right" }}>{fmt(values.part1.totals.subTotalCredit)}</td>
                  {values.part1.totals.otherVoucherValues.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                  <td style={{ ...s.td, textAlign: "right" }}>{fmt(values.part1.totals.totalCost)}</td>
                  {!containView && <td style={s.td}></td>}
              </tr>
            </tbody>
          </table>
        </div>
        {!containView && (
            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "10px" }}>
                <SoftButton variant="outlined" color="dark" size="small" onClick={addRowA}>+ Thêm dòng đoạn A</SoftButton>
                <SoftButton variant="outlined" color="dark" size="small" onClick={addRowB}>+ Thêm dòng đoạn B</SoftButton>
            </div>
        )}
      </TabPanel>

      {/* PART II */}
      <TabPanel value={tabIndex} index={1}>
        <div style={s.title}>Phần II: CHI PHÍ SẢN XUẤT, KINH DOANH THEO YẾU TỐ</div>
        <div style={{ textAlign: "center", marginBottom: "15px" }}>Tháng <input disabled={containView} style={s.dotted} value={values.month} onChange={e => setFieldValue("month", e.target.value)} /> năm <input disabled={containView} style={s.dotted} value={values.year} onChange={e => setFieldValue("year", e.target.value)} /></div>

        <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
                <tr>
                    <th style={{ ...s.th, width: "50px" }} rowSpan={2}>Số TT</th>
                    <th style={s.th} rowSpan={2}>Tên các tài khoản chi phí sản xuất, kinh doanh</th>
                    <th style={s.th} colSpan={5}>Yếu tố chi phí sản xuất, kinh doanh</th>
                    <th style={{ ...s.th, width: "100px" }} rowSpan={2}>Cộng</th>
                    <th style={{ ...s.th, width: "120px" }} rowSpan={2}>Luân chuyển nội bộ không tính vào chi phí SXKD</th>
                    <th style={{ ...s.th, width: "120px" }} rowSpan={2}>Tổng cộng chi phí</th>
                    {!containView && <th style={{ ...s.th, width: "40px" }} rowSpan={3}>#</th>}
                </tr>
                <tr>
                    <th style={s.th}>Chi phí Nguyên liệu, vật liệu</th>
                    <th style={s.th}>Chi phí nhân công</th>
                    <th style={s.th}>Chi phí Khấu hao TSCĐ</th>
                    <th style={s.th}>Chi phí dịch vụ mua ngoài</th>
                    <th style={s.th}>Chi phí khác bằng tiền</th>
                </tr>
                <tr style={{ background: "#eee", fontWeight: "bold" }}>
                    <th style={s.th}>A</th>
                    <th style={s.th}>B</th>
                    <th style={s.th}>1</th>
                    <th style={s.th}>2</th>
                    <th style={s.th}>3</th>
                    <th style={s.th}>4</th>
                    <th style={s.th}>5</th>
                    <th style={s.th}>6</th>
                    <th style={s.th}>7</th>
                    <th style={s.th}>8</th>
                </tr>
            </thead>
            <tbody>
                {values.part2.rows.map((r, i) => (
                    <tr key={i}>
                        <td style={s.td} align="center">{r.index}</td>
                        <td style={s.td}><input disabled={containView} style={{ ...s.input, textAlign: "left" }} value={r.accountName} onChange={e => setFieldValue(`part2.rows.${i}.accountName`, e.target.value)} /></td>
                        {r.elementValues.map((v, ci) => <td key={ci} style={s.td}><input disabled={containView} type="number" style={s.input} value={v} onChange={e => setFieldValue(`part2.rows.${i}.elementValues.${ci}`, e.target.value)} /></td>)}
                        <td style={{ ...s.td, textAlign: "right", background: "#f8f9fa" }}>{fmt(r.subTotal)}</td>
                        <td style={s.td}><input disabled={containView} type="number" style={s.input} value={r.internalTurnover} onChange={e => setFieldValue(`part2.rows.${i}.internalTurnover`, e.target.value)} /></td>
                        <td style={{ ...s.td, textAlign: "right", fontWeight: "bold", background: "#f0f2f5" }}>{fmt(r.totalCost)}</td>
                        {!containView && <td style={s.td} align="center"><button type="button" style={s.removeBtn} onClick={() => removeRow2(i)}>-</button></td>}
                    </tr>
                ))}
                <tr style={{ fontWeight: "bold", backgroundColor: "#e2e8f0" }}>
                    <td style={s.td} align="center">12</td>
                    <td style={s.td}>&nbsp; Cộng trong tháng</td>
                    {values.part2.totals.elementValues.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                    <td style={{ ...s.td, textAlign: "right" }}>{fmt(values.part2.totals.subTotal)}</td>
                    <td style={{ ...s.td, textAlign: "right" }}>{fmt(values.part2.totals.internalTurnover)}</td>
                    <td style={{ ...s.td, textAlign: "right" }}>{fmt(values.part2.totals.totalCost)}</td>
                    {!containView && <td style={s.td}></td>}
                </tr>
                <tr style={{ fontWeight: "bold", backgroundColor: "#cbd5e1" }}>
                    <td style={s.td} align="center">13</td>
                    <td style={s.td}>&nbsp; Luỹ kế từ đầu năm</td>
                    {values.part2.totals.accumulatedValues.map((v, i) => (
                        <td key={i} style={s.td}><input disabled={containView} type="number" style={s.input} value={v} onChange={e => {
                            const next = [...values.part2.totals.accumulatedValues];
                            next[i] = e.target.value;
                            setFieldValue(`part2.totals.accumulatedValues`, next);
                        }} /></td>
                    ))}
                    <td style={s.td}><input disabled={containView} type="number" style={s.input} value={values.part2.totals.accumulatedSubTotal} onChange={e => setFieldValue("part2.totals.accumulatedSubTotal", e.target.value)} /></td>
                    <td style={s.td}><input disabled={containView} type="number" style={s.input} value={values.part2.totals.accumulatedInternalTurnover} onChange={e => setFieldValue("part2.totals.accumulatedInternalTurnover", e.target.value)} /></td>
                    <td style={s.td}><input disabled={containView} type="number" style={s.input} value={values.part2.totals.accumulatedTotalCost} onChange={e => setFieldValue("part2.totals.accumulatedTotalCost", e.target.value)} /></td>
                    {!containView && <td style={s.td}></td>}
                </tr>
            </tbody>
        </table>
        {!containView && <div style={{ textAlign: "center", marginTop: "10px" }}><SoftButton variant="outlined" color="dark" size="small" onClick={addRow2}>+ Thêm dòng Phần II</SoftButton></div>}
      </TabPanel>

      {/* PART III */}
      <TabPanel value={tabIndex} index={2}>
        <div style={s.title}>Phần III: SỐ LIỆU CHI TIẾT PHẦN </div>
        <div style={{ ...s.title, fontSize: "12px", marginTop: "5px" }}>"LUÂN CHUYỂN NỘI BỘ KHÔNG TÍNH VÀO CHI PHÍ SẢN XUẤT, KINH DOANH"</div>
        <div style={{ textAlign: "center", marginBottom: "15px" }}>Tháng <input disabled={containView} style={s.dotted} value={values.month} onChange={e => setFieldValue("month", e.target.value)} /> năm <input disabled={containView} style={s.dotted} value={values.year} onChange={e => setFieldValue("year", e.target.value)} /></div>

        <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "1600px" }}>
                <thead>
                    <tr>
                        <th style={{ ...s.th, width: "50px" }} rowSpan={2}>Số TT</th>
                        <th style={s.th} rowSpan={2}>Tên các tài khoản chi phí sản xuất, kinh doanh</th>
                        <th style={s.th} colSpan={9}>Số liệu chi tiết các khoản luân chuyển nội bộ không tính vào chi phí SXKD</th>
                        <th style={{ ...s.th, width: "100px" }} rowSpan={2}>Cộng</th>
                        {!containView && <th style={{ ...s.th, width: "40px" }} rowSpan={3}>#</th>}
                    </tr>
                    <tr>
                        {values.part3.columnLabels.map((l, i) => (
                            <th key={i} style={s.th} width="150"><textarea disabled={containView} style={{ ...s.inputCenter, height: "60px", padding: "5px" }} value={l} onChange={e => {
                                const next = [...values.part3.columnLabels];
                                next[i] = e.target.value;
                                setFieldValue("part3.columnLabels", next);
                            }} /></th>
                        ))}
                    </tr>
                    <tr style={{ background: "#eee", fontWeight: "bold" }}>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        {Array.from({ length: 9 }).map((_, i) => <th key={i} style={s.th}>{i + 1}</th>)}
                        <th style={s.th}>10</th>
                    </tr>
                </thead>
                <tbody>
                    {values.part3.rows.map((r, i) => (
                        <tr key={i}>
                            <td style={s.td} align="center">{r.index}</td>
                            <td style={s.td}><input disabled={containView} style={{ ...s.input, textAlign: "left" }} value={r.accountName} onChange={e => setFieldValue(`part3.rows.${i}.accountName`, e.target.value)} /></td>
                            {r.detailValues.map((v, ci) => <td key={ci} style={s.td}><input disabled={containView} type="number" style={s.input} value={v} onChange={e => setFieldValue(`part3.rows.${i}.detailValues.${ci}`, e.target.value)} /></td>)}
                            <td style={{ ...s.td, textAlign: "right", fontWeight: "bold", background: "#f0f2f5" }}>{fmt(r.total)}</td>
                            {!containView && <td style={s.td} align="center"><button type="button" style={s.removeBtn} onClick={() => removeRow3(i)}>-</button></td>}
                        </tr>
                    ))}
                    <tr style={{ fontWeight: "bold", backgroundColor: "#e2e8f0" }}>
                        <td colSpan={2} style={{ ...s.td, paddingLeft: "10px" }}>Cộng</td>
                        {values.part3.columnTotals.map((v, i) => <td key={i} style={{ ...s.td, textAlign: "right" }}>{fmt(v)}</td>)}
                        <td style={{ ...s.td, textAlign: "right" }}>{fmt(values.part3.grandTotal)}</td>
                        {!containView && <td style={s.td}></td>}
                    </tr>
                </tbody>
            </table>
        </div>
        {!containView && <div style={{ textAlign: "center", marginTop: "10px" }}><SoftButton variant="outlined" color="dark" size="small" onClick={addRow3}>+ Thêm dòng Phần III</SoftButton></div>}
      </TabPanel>

      {/* FOOTER */}
      {/* <Box mt={3} p={3}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", textAlign: "center", gap: "20px" }}>
                <div>
                   <div style={{ fontWeight: "bold" }}>Đã ghi sổ Cái ngày... tháng... năm...</div>
                   <div style={{ fontWeight: "bold" }}>Người ghi sổ</div>
                   <div style={{ fontStyle: "italic" }}>(Ký, họ tên)</div>
                   <div style={{ marginTop: "60px", fontWeight: "bold" }}><input disabled={containView} style={s.inputCenter} value={values.ledgerClerk} onChange={e => setFieldValue("ledgerClerk", e.target.value)} /></div>
                </div>
                <div>
                   <div style={{ fontWeight: "bold" }}>&nbsp;</div>
                   <div style={{ fontWeight: "bold" }}>Kế toán trưởng</div>
                   <div style={{ fontStyle: "italic" }}>(Ký, họ tên)</div>
                   <div style={{ marginTop: "60px", fontWeight: "bold" }}><input disabled={containView} style={s.inputCenter} value={values.chiefAccountant} onChange={e => setFieldValue("chiefAccountant", e.target.value)} /></div>
                </div>
                <div>
                   <div style={{ fontStyle: "italic" }}>
                        Ngày <input disabled={containView} style={{...s.dotted, width: "30px"}} value={values.signingDay} onChange={e => setFieldValue("signingDay", e.target.value)} /> 
                        tháng <input disabled={containView} style={{...s.dotted, width: "30px"}} value={values.signingMonth} onChange={e => setFieldValue("signingMonth", e.target.value)} /> 
                        năm <input disabled={containView} style={{...s.dotted, width: "50px"}} value={values.signingYear} onChange={e => setFieldValue("signingYear", e.target.value)} />
                    </div>
                   <div style={{ fontWeight: "bold" }}>Người đại diện theo pháp luật</div>
                   <div style={{ fontStyle: "italic" }}>(Ký, họ tên, đóng dấu)</div>
                   <div style={{ marginTop: "60px", fontWeight: "bold" }}><input disabled={containView} style={s.inputCenter} value={values.legalRepresentative} onChange={e => setFieldValue("legalRepresentative", e.target.value)} /></div>
                </div>
            </div>
      </Box> */}
    </SoftBox>
  );
}
